// ---------------------------------------------------------------------------
// stream-parser — split a stream of SSE text deltas into a sequence of
// "marker ops" so the client can mirror the canonical /messages row shape
// during streaming.
//
// Why client-side
// ---------------
// The chat-bridge server stream (`chat-bridge/src/bot.ts → toWebTextStream`)
// yields ALL tokens for one assistant turn — text deltas AND tool-call
// boundaries — through a single SSE text stream.  The web UI was therefore
// rendering one big assistant bubble whose content was the full assistant
// response with `<<TOOL_CALL|status|name>>` markers interleaved between the
// text segments.
//
// The canonical `/api/sessions/:id/messages` endpoint, by contrast, returns
// distinct SQL rows: `[user, assistant_pre, tool_complete, assistant_post]`.
// Each row gets its own `kind: "message"` (or `kind: "tool"`) RenderItem
// wrapped in `<div className="px-4 pb-4">`, so the same conversation
// renders as 4 padded bubbles on history reload but ONE unpadded bubble
// during streaming.
//
// When streaming ended, `mergeMessages(..., { dropOptimistic: true })` in
// `main.tsx` swapped the SSE placeholder for the canonical rows — and the
// renderer saw a different array of items → visible layout "jump".
//
// The fix: split the in-flight SSE assistant placeholder at tool-call
// boundaries on the client, so the streaming tail produces the SAME row
// shape as the canonical replacement.  Once both paths emit
// `[user, assistant_pre, tool, assistant_post]`, `buildRenderItems` is
// shape-agnostic to its source and reconcile-time swaps are
// geometry-neutral.
//
// Marker grammar
// --------------
// Mirrors `chat-bridge/src/bot.ts → toWebTextStream`:
//
//   \n<<TOOL_CALL|start|{"version":1,"toolName":"...","details":{...}}>>\n
//   \n<<TOOL_CALL|end|{"version":1,"toolName":"...","details":{...}}>>\n
//   \n<<TOOL_CALL|error|{"version":1,"toolName":"...","details":{...}}>>\n
//
// Legacy status/name markers remain parseable, but only the versioned form
// carries structured live values.
//
// The split is marker-aware at the chunk boundary: we never cut mid-marker.
// Any unclosed recognized marker at the tail of the buffer is held for the
// next chunk.
// This guarantees `feed()` callers always see complete, well-formed markers
// even when the underlying SSE framing splits a marker across two text-delta
// chunks.
// ---------------------------------------------------------------------------

import {
  TOOL_DETAIL_FIELD_STATES,
  type LiveToolDetailPatch,
  type ToolDetailField,
} from "@/lib/tool-details";

/**
 * Marker regex — captured groups:
 *
 *   [1] status   — "start" | "end" | "error"
 *   [2] marker body — either a legacy tool name or a versioned JSON envelope
 *
 * Mirrors `ParsedMessageContent` in
 * `chat-bridge/ui/src/components/ai-elements/parsed-message.tsx`.  An
 * optional trailing pipe-segment present in that renderer is intentionally
 * dropped here. Structured live detail fields require the full marker body.
 */
export const TOOL_CALL_MARKER_RE =
  /<<TOOL_CALL\|(start|end|error)\|([\s\S]*?)>>/g;

/**
 * HITL paused marker — emitted by chat-bridge when an agent run pauses
 * for human confirmation. The payload is a JSON string with `runId`
 * and `requirements` (array of RunRequirement dicts).
 *
 *   \n<<HITL_PAUSED|{"runId":"...","requirements":[...]>>\n
 */
export const HITL_PAUSED_MARKER_RE =
  /<<HITL_PAUSED\|(.+?)>>/g;

/**
 * Compaction marker — emitted by chat-bridge when context compaction fires
 * mid-run. The payload is a JSON string with `tokensBefore` and
 * `summaryLength` (token counts only — the summary preview is not sent on
 * the live path to avoid regex-breaking characters).
 *
 *   \n<<COMPACTION|{"tokensBefore":50000,"summaryLength":2000}>>\n
 */
export const COMPACTION_MARKER_RE =
  /<<COMPACTION\|(.+?)>>/g;

export type StreamMarkerOp =
  | { type: "text"; text: string }
  | {
      type: "tool_marker";
      status: "start" | "end" | "error";
      toolName: string;
      /** Structured data carried only by the live stream envelope. */
      details?: LiveToolDetailPatch;
    }
  | {
      type: "hitl_paused";
      runId: string;
      requirements: RunRequirementInfo[];
    }
  | {
      type: "compaction_marker";
      tokensBefore: number;
      summaryLength: number;
    }
  | { type: "flash_mode" };

/**
 * HITL explanation generated by the LLM explainer (hitl_explainer.py).
 * Attached to the requirement via monkey-patching to_dict, so it flows
 * through both the live SSE stream and the DB marker row.
 */
export interface HitlExplanation {
  summary: string;
  risk_level: "critical" | "high" | "medium" | "low";
  is_destructive: boolean;
  is_modifying: boolean;
  warnings: string[];
}

/**
 * Non-secret UI metadata for a human-supplied secret.  The descriptor never
 * contains the entered value; the password lives only in the approval input
 * DOM until the separate continuation request is made.
 */
export interface HumanSecretInputDescriptor {
  requirement_id: string;
  required: boolean;
  label: string;
  help?: string;
  max_length?: number;
}

/**
 * Minimal shape of a RunRequirement as serialized by agno's
 * RunRequirement.to_dict() — just the fields the UI needs.
 */
export interface RunRequirementInfo {
  id: string;
  tool_execution?: {
    tool_name: string;
    tool_args?: Record<string, unknown>;
    tool_call_id?: string;
    requires_confirmation?: boolean;
    confirmed?: boolean | null;
  };
  confirmation?: boolean | null;
  member_agent_name?: string;
  explanation?: HitlExplanation;
  human_secret_input?: HumanSecretInputDescriptor;
}

function parseHumanSecretInput(
  value: unknown,
  requirementId: string,
): HumanSecretInputDescriptor | undefined {
  if (!isRecord(value)) return undefined;
  const label = typeof value.label === "string" && value.label.trim()
    ? value.label.slice(0, 120)
    : "Secret";
  const descriptor: HumanSecretInputDescriptor = {
    requirement_id: requirementId,
    required: value.required !== false,
    label,
  };
  if (typeof value.help === "string" && value.help) {
    descriptor.help = value.help.slice(0, 500);
  }
  if (typeof value.max_length === "number" && Number.isSafeInteger(value.max_length)) {
    descriptor.max_length = Math.max(1, Math.min(value.max_length, 4096));
  }
  // Deliberately do not copy value.value (or any other unknown field).
  return descriptor;
}

function parseRequirementInfo(value: unknown): RunRequirementInfo | null {
  if (!isRecord(value)) return null;
  const id = typeof value.id === "string" ? value.id : "";
  const rawTool = isRecord(value.tool_execution) ? value.tool_execution : undefined;
  const toolExecution = rawTool && typeof rawTool.tool_name === "string"
    ? {
        tool_name: rawTool.tool_name,
        ...(isRecord(rawTool.tool_args) ? { tool_args: rawTool.tool_args } : {}),
        ...(typeof rawTool.tool_call_id === "string"
          ? { tool_call_id: rawTool.tool_call_id.slice(0, 256) }
          : {}),
        ...(typeof rawTool.requires_confirmation === "boolean"
          ? { requires_confirmation: rawTool.requires_confirmation }
          : {}),
        ...(typeof rawTool.confirmed === "boolean" || rawTool.confirmed === null
          ? { confirmed: rawTool.confirmed }
          : {}),
      }
    : undefined;
  const requirement: RunRequirementInfo = { id };
  if (toolExecution) requirement.tool_execution = toolExecution;
  if (typeof value.confirmation === "boolean" || value.confirmation === null) {
    requirement.confirmation = value.confirmation;
  }
  if (typeof value.member_agent_name === "string") {
    requirement.member_agent_name = value.member_agent_name;
  }
  if (isRecord(value.explanation)) {
    const explanation = value.explanation;
    if (
      typeof explanation.summary === "string" &&
      typeof explanation.risk_level === "string" &&
      ["critical", "high", "medium", "low"].includes(explanation.risk_level) &&
      typeof explanation.is_destructive === "boolean" &&
      typeof explanation.is_modifying === "boolean" &&
      Array.isArray(explanation.warnings)
    ) {
      requirement.explanation = {
        summary: explanation.summary,
        risk_level: explanation.risk_level as HitlExplanation["risk_level"],
        is_destructive: explanation.is_destructive,
        is_modifying: explanation.is_modifying,
        warnings: explanation.warnings.filter((item): item is string => typeof item === "string"),
      };
    }
  }
  const descriptor = parseHumanSecretInput(value.human_secret_input, id);
  if (descriptor) requirement.human_secret_input = descriptor;
  return requirement;
}

/**
 * Maximum size of a non-tool "pending" tail (the region from an unclosed
 * marker to the buffer end). Tool-call envelopes have their own explicit
 * 64 KiB UTF-8 transport bound below.
 */
const MAX_PENDING_HOLD = 256;
const MAX_TOOL_MARKER_BYTES = 64 * 1024;
const MAX_TOOL_NAME_LENGTH = 256;

function utf8Bytes(value: string): number {
  return new TextEncoder().encode(value).byteLength;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function invalidDetailsFor(status: "start" | "end" | "error"): LiveToolDetailPatch {
  return status === "start"
    ? { parameters: { state: "malformed" } }
    : {
        result: { state: "malformed" },
        error: { state: "malformed" },
      };
}

function truncatedDetailsFor(status: "start" | "end" | "error"): LiveToolDetailPatch {
  return status === "start"
    ? { parameters: { state: "truncated" } }
    : {
        result: { state: "truncated" },
        error: { state: "truncated" },
      };
}

function parseDetailField(value: unknown): ToolDetailField | null {
  if (!isRecord(value) || !TOOL_DETAIL_FIELD_STATES.includes(value.state as any)) {
    return null;
  }
  const state = value.state as ToolDetailField["state"];
  if (state === "available") {
    if (!Object.prototype.hasOwnProperty.call(value, "value")) return null;
    return { state, value: value.value };
  }
  return { state };
}

function parseToolMarker(
  status: "start" | "end" | "error",
  body: string,
): Extract<StreamMarkerOp, { type: "tool_marker" }> {
  if (utf8Bytes(body) > MAX_TOOL_MARKER_BYTES) {
    return { type: "tool_marker", status, toolName: "unknown", details: truncatedDetailsFor(status) };
  }

  // Old server markers had the raw tool name as their final segment. Keep
  // them status-only so old recordings/replays remain harmless and usable.
  if (!body.startsWith("{")) {
    const toolName = body.trim();
    if (toolName && !toolName.includes("|") && !toolName.includes(">")) {
      return { type: "tool_marker", status, toolName: toolName.slice(0, MAX_TOOL_NAME_LENGTH) };
    }
    return { type: "tool_marker", status, toolName: "unknown", details: invalidDetailsFor(status) };
  }

  try {
    const envelope = JSON.parse(body) as unknown;
    if (
      !isRecord(envelope) ||
      envelope.version !== 1 ||
      typeof envelope.toolName !== "string" ||
      !envelope.toolName.trim() ||
      !isRecord(envelope.details)
    ) {
      throw new Error("invalid tool marker envelope");
    }

    const details: LiveToolDetailPatch = {};
    for (const key of ["parameters", "result", "error"] as const) {
      if (!Object.prototype.hasOwnProperty.call(envelope.details, key)) continue;
      const field = parseDetailField(envelope.details[key]);
      if (field === null) {
        return {
          type: "tool_marker",
          status,
          toolName: envelope.toolName.slice(0, MAX_TOOL_NAME_LENGTH),
          details: invalidDetailsFor(status),
        };
      }
      details[key] = field;
    }

    return {
      type: "tool_marker",
      status,
      toolName: envelope.toolName.slice(0, MAX_TOOL_NAME_LENGTH),
      details,
    };
  } catch {
    // Consume malformed structured markers rather than exposing their raw
    // bytes as assistant prose. The badge can truthfully show unavailable
    // structured data after an explicit user action.
    return { type: "tool_marker", status, toolName: "unknown", details: invalidDetailsFor(status) };
  }
}

const MARKER_HEADS = ["<<TOOL_CALL", "<<HITL_PAUSED", "<<COMPACTION", "<<FLASH_MODE"];

/**
 * Locate an actual unclosed marker at the tail. We walk complete markers from
 * left to right instead of using `lastIndexOf("<<")`: live JSON values may
 * legitimately contain `<<TOOL_CALL` text and must not split their envelope.
 */
function findUnclosedMarkerStart(buffer: string): number {
  let cursor = 0;
  while (cursor < buffer.length) {
    const start = buffer.indexOf("<<", cursor);
    if (start === -1) return -1;
    const tail = buffer.slice(start);
    const markerLike = MARKER_HEADS.some(
      (head) => tail.startsWith(head) || head.startsWith(tail),
    );
    if (!markerLike) {
      cursor = start + 2;
      continue;
    }
    const close = buffer.indexOf(">>", start + 2);
    if (close === -1) return start;
    cursor = close + 2;
  }
  return -1;
}

/**
 * Streaming marker parser.  Stateful — accumulates an internal buffer
 * across `feed()` calls so a marker split across two chunks is still
 * recognised as one op once the chunk boundary closes it.
 *
 * Lifecycle:
 *
 *   const parser = new StreamMarkerParser();
 *   for chunk of stream:
 *     applyOps(parser.feed(chunk));
 *   applyOps(parser.flush());   // drain residual buffer as plain text
 *
 * `feed()` returns ops that have become unconditional.  Any unclosed
 * prefix at the tail (`<<` without a matching `>>` after it) is held
 * for the next call so we never emit half a marker as plain text.
 */
export class StreamMarkerParser {
  private buffer = "";
  private discardingOversizedToolMarker = false;

  /**
   * Feed a delta.  Returns ops that have now become safe to apply.
   * Holds any partial marker prefix at the tail of the buffer for the
   * next call.  An empty/null/undefined `delta` is a no-op.
   */
  feed(delta: string | null | undefined): StreamMarkerOp[] {
    if (!delta) return [];

    if (this.discardingOversizedToolMarker) {
      const close = delta.indexOf(">>");
      if (close === -1) return [];
      this.discardingOversizedToolMarker = false;
      delta = delta.slice(close + 2);
      if (!delta) return [];
    }

    this.buffer += delta;

    // Any text before an unclosed marker is safe to emit. Marker heads are
    // recognized structurally so `<<` inside a JSON tool value stays data.
    const pendingStart = findUnclosedMarkerStart(this.buffer);
    const cutAt = pendingStart === -1 ? this.buffer.length : pendingStart;

    const pendingMarker = this.buffer.slice(cutAt);
    const pending = pendingMarker.startsWith("<<TOOL_CALL")
      ? utf8Bytes(pendingMarker)
      : pendingMarker.length;
    const pendingLimit = pendingMarker.startsWith("<<TOOL_CALL")
      ? MAX_TOOL_MARKER_BYTES
      : MAX_PENDING_HOLD;
    if (pending > pendingLimit) {
      if (pendingMarker.startsWith("<<TOOL_CALL")) {
        // A legitimate live envelope is capped by the bridge. Do not turn an
        // oversized/unclosed marker into visible assistant text; discard it
        // through its closing delimiter and surface a bounded badge state.
        const match = /^<<TOOL_CALL\|(start|end|error)\|/.exec(pendingMarker);
        const safe = this.buffer.slice(0, cutAt);
        this.buffer = "";
        this.discardingOversizedToolMarker = true;
        const out = safe ? parseEmitBuffer(safe) : [];
        if (match) {
          out.push({
            type: "tool_marker",
            status: match[1] as "start" | "end" | "error",
            toolName: "unknown",
            details: truncatedDetailsFor(match[1] as "start" | "end" | "error"),
          });
        }
        return out;
      }
      // Malformed stream: pending prefix never closed and is now too
      // long to plausibly be a marker prefix.  Force-emit everything
      // except the tail-held `MAX_PENDING_HOLD` chars as plain text so
      // the UI keeps making progress (the residual tail still waits
      // for a closing `>>`, or will be force-emitted by flush()).
      const dropBytes = pending - MAX_PENDING_HOLD;
      const safe = this.buffer.slice(0, cutAt);
      const head = this.buffer.slice(cutAt, cutAt + dropBytes);
      this.buffer = this.buffer.slice(cutAt + dropBytes);
      const out = safe ? parseEmitBuffer(safe) : [];
      if (head) out.push({ type: "text", text: head });
      return out;
    }

    if (cutAt === 0) return [];

    const emitBuf = this.buffer.slice(0, cutAt);
    this.buffer = this.buffer.slice(cutAt);
    return parseEmitBuffer(emitBuf);
  }

  /**
   * Drain any residual buffer at stream end.  Parse any complete
   * tool-call markers out of the residual as tool_marker ops; emit the
   * remainder as plain text — with one exception: if the buffer ends
   * with an unclosed partial-marker prefix that aligns with an actual
   * `<<TOOL_CALL` start, drop the partial bytes so `<<TOOL_CALL|...`
   * never leaks into a visible assistant bubble.  Without this trim, a
   * stream truncated mid-marker (server crash, malformed SSE, network
   * drop just after a delta) would render the raw
   * `<<TOOL_CALL|end|ls` fragment as bubble text.
   *
   * The prefix check looks for the literal `<<TOOL_CALL` substring
   * rather than a generic unbalanced `<<`.  Unbalanced `<<` is also
   * produced by natural text — `cout <<` in C++ discussion, Ruby
   * heredoc syntax, mathematical inequalities — and aggressively
   * trimming those would silently swallow legitimate response
   * content.  The `<<TOOL_CALL` substring is high-cardinality enough
   * that the substring filter is reliable.
   */
  flush(): StreamMarkerOp[] {
    if (this.discardingOversizedToolMarker) {
      this.buffer = "";
      this.discardingOversizedToolMarker = false;
      return [];
    }
    if (!this.buffer) return [];
    const buf = this.buffer;

    const unclosedStart = findUnclosedMarkerStart(buf);
    if (unclosedStart !== -1) {
      const safe = buf.slice(0, unclosedStart);
      this.buffer = "";
      return safe ? parseEmitBuffer(safe) : [];
    }

    this.buffer = "";
    return parseEmitBuffer(buf);
  }
}

/**
 * Walk `s` left-to-right: emit any text BETWEEN markers as `text` ops,
 * and emit each marker as a `tool_marker`, `hitl_paused`, `compaction_marker`,
 * or `flash_mode` op. The caller has already guaranteed that every
 * marker in `s` is complete (the buffer was truncated at the rightmost
 * unclosed `<<`).
 *
 * We match ALL marker types — `<<TOOL_CALL|...>>`, `<<HITL_PAUSED|...>>`,
 * and `<<COMPACTION|...>>` — in a single pass.  A combined regex avoids
 * position conflicts when multiple marker types appear in the same emit
 * buffer.
 */
function parseEmitBuffer(s: string): StreamMarkerOp[] {
  const out: StreamMarkerOp[] = [];
  // Combined regex — matches TOOL_CALL, HITL_PAUSED, COMPACTION, or FLASH_MODE markers.
  // Group 1: TOOL_CALL status  (when present)
  // Group 2: TOOL_CALL toolName (when present)
  // Group 3: HITL_PAUSED payload JSON (when present)
  // Group 4: COMPACTION payload JSON (when present)
  const combinedRe = /<<TOOL_CALL\|(start|end|error)\|([\s\S]*?)>>|<<HITL_PAUSED\|(.+?)>>|<<COMPACTION\|(.+?)>>|<<FLASH_MODE>>/g;
  let lastEnd = 0;
  let m: RegExpExecArray | null;
  while ((m = combinedRe.exec(s)) !== null) {
    if (m.index > lastEnd) {
      out.push({ type: "text", text: s.slice(lastEnd, m.index) });
    }
    if (m[1] !== undefined) {
      out.push(parseToolMarker(
        m[1] as "start" | "end" | "error",
        m[2],
      ));
    } else if (m[3] !== undefined) {
      // HITL_PAUSED marker — parse the JSON payload.
      // The payload has ">>" escaped as "\u003E\u003E" (see bot.ts
      // toWebTextStream) to prevent the marker regex from matching
      // ">>" inside the JSON.  JSON.parse handles \u003E natively,
      // so no explicit unescape is needed here.
      try {
        const payload = JSON.parse(m[3]);
        const requirements = Array.isArray(payload.requirements)
          ? payload.requirements
              .map(parseRequirementInfo)
              .filter((item: RunRequirementInfo | null): item is RunRequirementInfo => item !== null)
          : [];
        out.push({
          type: "hitl_paused",
          runId: typeof payload.runId === "string" ? payload.runId : "",
          requirements,
        });
      } catch {
        // Malformed JSON — emit as plain text so it's visible
        out.push({ type: "text", text: m[0] });
      }
    } else if (m[4] !== undefined) {
      // COMPACTION marker — parse the JSON payload
      try {
        const payload = JSON.parse(m[4]);
        out.push({
          type: "compaction_marker",
          tokensBefore: typeof payload.tokensBefore === "number" ? payload.tokensBefore : 0,
          summaryLength: typeof payload.summaryLength === "number" ? payload.summaryLength : 0,
        });
      } catch {
        // Malformed JSON — emit as plain text so it's visible
        out.push({ type: "text", text: m[0] });
      }
    } else {
      out.push({ type: "flash_mode" });
    }
    lastEnd = m.index + m[0].length;
  }
  if (lastEnd < s.length) {
    out.push({ type: "text", text: s.slice(lastEnd) });
  }
  return out;
}
